import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import apiService from '../../../services/apiService';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { formatCurrency } from '../../../utils/formatting';
import { formatDueDate, isOverdue as isTaskOverdue, toDateInputValue, toDueDatePayload } from '../../../utils/taskBoard';
import UserSearch from '../../shared/UserSearch';
import CancelTaskConfirm from '../../shared/CancelTaskConfirm';
import useUnitActivities from '../../../hooks/useUnitActivities';
import useQuickGuard from '../../../hooks/useQuickGuard';
import useUndoToast from '../../../hooks/useUndoToast';
import TaskUndoToast from '../../shared/TaskUndoToast';
import { undoToastText } from '../../../utils/taskBoard';
import {
    ACTIVITY_STATUSES, ACTIVITY_TYPES, AI_DESCRIPTION_SEPARATOR, buildCreatePayload, emptyActivitiesText,
    filterActivities, filterLabel, fmtActivityDate, isOpenActivity, statusChangeFields, ACTIVITY_FILTERS,
} from '../../../utils/unitActivities';

// Type / status labels and colours, the payload and the status rules live in
// utils/unitActivities.js, shared with the full unit page (UnitV2).
const TYPE_CONFIG = ACTIVITY_TYPES;
const STATUS_CONFIG = ACTIVITY_STATUSES;
const fmtDate = fmtActivityDate;

// ── Create Request Modal ─────────────────────────────────────────────────────

// `draft` / `onDraft`: what was typed survives closing the form with Escape
// or a click outside; only "Discard" and a successful create drop it.
function CreateRequestModal({
    unit, onClose, onCreated, activities: parentActivities, draft = null, onDraft = () => {}, onDiscard,
}) {
    const [type, setType] = useState(draft?.type ?? 'price_change');
    const [title, setTitle] = useState(draft?.title ?? '');
    const [details, setDetails] = useState(draft?.details ?? '');
    const [suggestedPrice, setSuggestedPrice] = useState(draft?.suggestedPrice ?? '');
    const [assignedTo, setAssignedTo] = useState(draft?.assignedTo ?? '');
    const [dueDate, setDueDate] = useState(draft?.dueDate ?? '');
    useEffect(() => {
        onDraft({ type, title, details, suggestedPrice, assignedTo, dueDate });
    }, [type, title, details, suggestedPrice, assignedTo, dueDate]); // eslint-disable-line react-hooks/exhaustive-deps

    // Escape closes the form only: it must not reach the unit pane, which
    // closes on Escape too (D6: the innermost layer first).
    const onKeyDown = (e) => {
        if (e.key !== 'Escape') return;
        e.stopPropagation();
        onClose();
    };
    const [users, setUsers] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        apiService.getActivityUsers().then(data => setUsers(data.users || [])).catch(() => {});
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!title.trim()) return;
        setSubmitting(true);
        try {
            await apiService.createActivity(buildCreatePayload(unit, {
                type, title, details, suggestedPrice, assignedTo, dueDate,
            }));
            onCreated();
            onDiscard?.();
            onClose();
        } catch (err) {
            console.error('Failed to create activity', err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="activity-modal-overlay" onClick={onClose} onKeyDown={onKeyDown}>
            <div className="activity-modal" onClick={(e) => e.stopPropagation()}>
                <div className="activity-modal-header">
                    <h3>Create Request</h3>
                    <span className="activity-modal-unit">Stock #{unit.StockNumber}</span>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="activity-form-group">
                        <label>Type</label>
                        <select value={type} onChange={(e) => setType(e.target.value)}>
                            <option value="price_change">Price Change</option>
                            <option value="photo_update">Photo Update</option>
                            <option value="description">Description Improvement</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                    <div className="activity-form-group">
                        <label>Title</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g. Reduce price to $350,000"
                            required
                            maxLength={800}
                        />
                    </div>
                    {type === 'price_change' && (
                        <div className="activity-form-row">
                            <div className="activity-form-group">
                                <label>Current Price</label>
                                <input type="text" value={formatCurrency(unit.Best_Sell_Price)} disabled />
                            </div>
                            <div className="activity-form-group">
                                <label>Suggested Price</label>
                                <input
                                    type="number"
                                    value={suggestedPrice}
                                    onChange={(e) => setSuggestedPrice(e.target.value)}
                                    placeholder="350000"
                                    min={0}
                                    step={1000}
                                />
                            </div>
                        </div>
                    )}
                    <div className="activity-form-row">
                        <div className="activity-form-group">
                            <label>Assign to</label>
                            <UserSearch
                                users={users}
                                value={assignedTo}
                                onChange={setAssignedTo}
                                activities={parentActivities}
                                placeholder="Search users..."
                            />
                        </div>
                        <div className="activity-form-group">
                            <label>Due date</label>
                            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                        </div>
                    </div>
                    <div className="activity-form-group">
                        <label>Notes</label>
                        <textarea
                            value={details}
                            onChange={(e) => setDetails(e.target.value)}
                            placeholder="Additional context..."
                            rows={3}
                        />
                    </div>
                    <div className="activity-form-actions">
                        {/* "Discard", not "Cancel": "Cancel" is reserved for the
                            task status, so the two are never confused. */}
                        <button type="button" className="activity-btn-cancel" onClick={() => { onDiscard?.(); onClose(); }}>Discard</button>
                        <button type="submit" className="activity-btn-create" disabled={submitting || !title.trim()}>
                            {submitting ? 'Creating...' : 'Create Request'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ── Activity Card ────────────────────────────────────────────────────────────

function ActivityCard({ activity, onStatusChange, onQuickStatus, onAssign, onEdit, onDelete, users, activities, canEdit }) {
    const [editing, setEditing] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [confirmCancelTask, setConfirmCancelTask] = useState(false);
    // After "Keep task" the prompt unmounts; focus returns to "Cancel task" so
    // keyboard users keep their place instead of dropping to <body>.
    const cancelTaskBtnRef = useRef(null);
    const refocusCancelTask = useRef(false);
    useEffect(() => {
        if (!confirmCancelTask && refocusCancelTask.current) {
            refocusCancelTask.current = false;
            cancelTaskBtnRef.current?.focus();
        }
    }, [confirmCancelTask]);
    const [editTitle, setEditTitle] = useState(activity.title);
    const [editDetails, setEditDetails] = useState(activity.details || '');
    const [editDueDate, setEditDueDate] = useState(toDateInputValue(activity.due_date));
    const typeConf = TYPE_CONFIG[activity.type] || TYPE_CONFIG.other;
    const statusConf = STATUS_CONFIG[activity.status] || STATUS_CONFIG.open;
    const isOpen = activity.status === 'open' || activity.status === 'in_progress';
    const isClosed = activity.status === 'completed' || activity.status === 'cancelled';
    // Actionable in principle, and actionable by *this* caller: a read-only
    // workspace is one the backend will refuse every write on. Overdue is a fact
    // about the activity, so it keeps flagging either way.
    const isActionable = canEdit && isOpen;
    // A due date is a calendar day: late from the day after, not from UTC midnight.
    const isOverdue = isTaskOverdue(activity);

    // Editing the task's fields is independent of its status: a cancelled or
    // completed task can still be corrected. Status changes live in their own
    // row (Start / Complete / Cancel task, or Reopen task).
    const startEditing = () => {
        setEditTitle(activity.title);
        setEditDetails(activity.details || '');
        setEditDueDate(toDateInputValue(activity.due_date));
        setConfirmCancelTask(false);
        setEditing(true);
    };

    // Discarding throws the unsaved edits away — it never touches the status.
    const handleDiscardEdit = () => {
        setEditTitle(activity.title);
        setEditDetails(activity.details || '');
        setEditDueDate(toDateInputValue(activity.due_date));
        setEditing(false);
    };

    const handleSaveEdit = () => {
        onEdit(activity.id, {
            title: editTitle,
            details: editDetails,
            due_date: toDueDatePayload(editDueDate),
        });
        setEditing(false);
    };

    return (
        <div className={`activity-card ${isOverdue ? 'activity-card-overdue' : ''}`}>
            <div className="activity-card-header">
                <span className="activity-type-badge" style={{ color: typeConf.color, backgroundColor: typeConf.bg }}>
                    {typeConf.label}
                </span>
                <div className="activity-header-right">
                    {canEdit && !editing && (
                        <button className="activity-edit-btn" onClick={(e) => { e.stopPropagation(); startEditing(); }} title="Edit">
                            <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                            </svg>
                        </button>
                    )}
                    {canEdit && !editing && (
                        confirmDelete ? (
                            <div className="activity-delete-confirm">
                                <span className="activity-delete-confirm-text">Delete?</span>
                                <button className="activity-delete-confirm-yes" onClick={(e) => { e.stopPropagation(); onDelete(activity.id); }}>Yes</button>
                                <button className="activity-delete-confirm-no" onClick={(e) => { e.stopPropagation(); setConfirmDelete(false); }}>No</button>
                            </div>
                        ) : (
                            <button className="activity-delete-btn" onClick={(e) => { e.stopPropagation(); setConfirmDelete(true); }} title="Delete">
                                <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                            </button>
                        )
                    )}
                    <span className="activity-status-badge" style={{ color: statusConf.color, backgroundColor: statusConf.bg, borderColor: statusConf.border }}>
                        {statusConf.label}
                    </span>
                </div>
            </div>
            {editing ? (
                <div className="activity-edit-form">
                    <div className="activity-form-group">
                        <label>Title</label>
                        <input type="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} maxLength={800} />
                    </div>
                    <div className="activity-form-group">
                        <label>Due date</label>
                        <input type="date" value={editDueDate} onChange={(e) => setEditDueDate(e.target.value)} />
                    </div>
                    <div className="activity-form-group">
                        <label>Details</label>
                        <textarea value={editDetails} onChange={(e) => setEditDetails(e.target.value)} rows={3} />
                    </div>
                    <div className="activity-edit-actions">
                        <button type="button" className="activity-btn-cancel" onClick={handleDiscardEdit}>Discard changes</button>
                        <button type="button" className="activity-btn-create" onClick={handleSaveEdit} disabled={!editTitle.trim()}>Save changes</button>
                    </div>
                </div>
            ) : (
            <>
            <p className="activity-card-title">{activity.title}</p>
            {activity.details && (() => {
                const separator = AI_DESCRIPTION_SEPARATOR;
                const hasDesc = activity.details.includes(separator);
                if (!hasDesc) {
                    return <p className="activity-card-details">{activity.details}</p>;
                }
                const [rationale, aiDesc] = activity.details.split(separator);
                return (
                    <>
                        {rationale.trim() && <p className="activity-card-details">{rationale.trim()}</p>}
                        <div className="activity-ai-description">
                            <div className="activity-ai-desc-header">
                                <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                AI-Generated Description
                                <button className="activity-copy-btn" onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(aiDesc.trim());
                                }}>Copy</button>
                            </div>
                            <p className="activity-ai-desc-text">{aiDesc.trim()}</p>
                        </div>
                    </>
                );
            })()}
            {activity.type === 'price_change' && activity.suggested_price != null && (
                <div className="activity-price-row">
                    {activity.current_price != null && <span>{formatCurrency(activity.current_price)}</span>}
                    <span className="activity-price-arrow">→</span>
                    <span className="activity-price-suggested">{formatCurrency(activity.suggested_price)}</span>
                </div>
            )}
            <div className="activity-card-meta">
                <span>{activity.created_by}</span>
                <span className="activity-meta-sep">→</span>
                {isActionable ? (
                    <span onClick={(e) => e.stopPropagation()}>
                        <UserSearch
                            users={users}
                            value={activity.assigned_to || ''}
                            onChange={(val) => onAssign(activity.id, val || null)}
                            activities={activities}
                            placeholder="Assign..."
                            variant="inline"
                        />
                    </span>
                ) : activity.assigned_to ? (
                    <span>{activity.assigned_to}</span>
                ) : (
                    <span className="activity-unassigned">Unassigned</span>
                )}
                <span className="activity-meta-sep">·</span>
                <span>{fmtDate(activity.created_at)}</span>
                {activity.due_date && (
                    <>
                        <span className="activity-meta-sep">·</span>
                        <span className={isOverdue ? 'activity-overdue-text' : ''}>Due {formatDueDate(activity.due_date)}</span>
                    </>
                )}
            </div>
            {activity.completed_at && (
                <div className="activity-card-completed">
                    Completed {fmtDate(activity.completed_at)} by {activity.completed_by}
                </div>
            )}
            {isActionable && (
                <div className="activity-card-actions">
                    {confirmCancelTask ? (
                        // Cancelling the task is a status change, so it asks first —
                        // it must never be mistaken for leaving an edit.
                        <CancelTaskConfirm
                            classPrefix="activity"
                            buttonClass="activity-action-btn"
                            dangerClass="activity-action-cancel"
                            onKeep={() => { refocusCancelTask.current = true; setConfirmCancelTask(false); }}
                            onConfirm={() => { setConfirmCancelTask(false); onStatusChange(activity.id, 'cancelled'); }}
                        />
                    ) : (
                        <>
                            {activity.status === 'open' && (
                                <button type="button" className="activity-action-btn" onClick={(e) => onQuickStatus(activity.id, 'in_progress', e)}>
                                    Start
                                </button>
                            )}
                            <button type="button" className="activity-action-btn activity-action-complete" onClick={(e) => onQuickStatus(activity.id, 'completed', e)}>
                                Complete
                            </button>
                            <button type="button" ref={cancelTaskBtnRef} className="activity-action-btn activity-action-cancel" onClick={() => setConfirmCancelTask(true)}>
                                Cancel task
                            </button>
                        </>
                    )}
                </div>
            )}
            {canEdit && isClosed && (
                <div className="activity-card-actions">
                    <button type="button" className="activity-action-btn activity-action-reopen" onClick={(e) => onQuickStatus(activity.id, 'open', e)}>
                        Reopen task
                    </button>
                </div>
            )}
            </>
            )}
        </div>
    );
}

// ── Main Tab ─────────────────────────────────────────────────────────────────

function ActivityTab({ unit, openCreate = false, activitiesState = null }) {
    // SAG staff look at a customer's dealership read-only. The backend refuses
    // every write below, so none of them are offered — a button that can only
    // ever answer 403 reads as a broken app.
    const { readOnly } = useWorkspace();
    const canEdit = !readOnly;
    // The list and its versioning (hooks/useUnitActivities). The full unit
    // page hands in the instance it already holds for the hero's task chip.
    const own = useUnitActivities(unit.StockNumber, { enabled: !activitiesState });
    const {
        activities, loading, error: listError, fetchActivities, applyUpdate, removeLocal,
    } = activitiesState || own;
    // `openCreate`: arrived via the inventory's "Create task" for this unit.
    const [showCreate, setShowCreate] = useState(openCreate && canEdit);
    // The create form's unsent input (kept when the form is closed).
    const createDraft = useRef(null);
    const [filter, setFilter] = useState('all');
    const [users, setUsers] = useState([]);

    useEffect(() => { apiService.getActivityUsers().then(d => setUsers(d.users || [])).catch(() => {}); }, []);

    const handleAssign = async (activityId, assignee) => {
        try {
            const updates = { assigned_to: assignee };
            const saved = await apiService.updateActivity(activityId, updates);
            applyUpdate(activityId, saved, updates);
        } catch (err) {
            console.error('Failed to assign', err);
        }
    };

    const handleEdit = async (activityId, updates) => {
        try {
            const saved = await apiService.updateActivity(activityId, updates);
            applyUpdate(activityId, saved, updates);
        } catch (err) {
            console.error('Failed to edit', err);
        }
    };

    const handleDelete = async (activityId) => {
        try {
            await apiService.deleteActivity(activityId);
            removeLocal(activityId);
        } catch (err) {
            console.error('Failed to delete activity', err);
        }
    };

    // D11: the board's Undo toast and 400 ms quick guard (shared hooks).
    const quickGuard = useQuickGuard();
    const undoToast = useUndoToast();
    const statusChangeRef = useRef(null);
    const handleStatusChange = async (activityId, newStatus, { silent = false } = {}) => {
        const prevStatus = activities.find((a) => a.id === activityId)?.status;
        try {
            const saved = await apiService.updateActivityStatus(activityId, { status: newStatus });
            // As the server does: the completion stamp belongs to "completed" only.
            const sent = statusChangeFields(newStatus);
            applyUpdate(activityId, saved, sent);
            const text = silent ? null : undoToastText(prevStatus, newStatus);
            if (text) {
                undoToast.offer({
                    text,
                    onUndo: () => statusChangeRef.current(activityId, prevStatus, { silent: true }),
                });
            }
        } catch (err) {
            console.error('Failed to update activity', err);
        }
    };
    statusChangeRef.current = handleStatusChange;
    // Start / Complete / Reopen: never the second click of a double-click,
    // never within 400 ms of the previous one.
    const handleQuickStatus = (activityId, newStatus, e) => {
        if (!quickGuard(e)) return;
        handleStatusChange(activityId, newStatus);
    };

    const filtered = filterActivities(activities, filter);

    const openCount = activities.filter(isOpenActivity).length;

    if (loading) {
        return (
            <div className="tab-content">
                <div className="pipeline-loading">Loading activities...</div>
            </div>
        );
    }

    // A failed read is an error with Retry, never "No activities yet" (QA P-2).
    if (listError) {
        return (
            <div className="tab-content">
                <div className="activity-empty" role="alert">
                    <p>{listError}</p>
                    <button type="button" className="activity-create-btn-empty" onClick={() => fetchActivities()}>Retry</button>
                </div>
            </div>
        );
    }

    return (
        <div className="tab-content">
            <div className="activity-header">
                <div className="activity-header-left">
                    <h3 className="activity-title">
                        Activities
                        {openCount > 0 && <span className="activity-open-count">{openCount} open</span>}
                    </h3>
                    <div className="activity-filters">
                        {ACTIVITY_FILTERS.map(f => (
                            <button
                                key={f}
                                className={`activity-filter-btn ${filter === f ? 'active' : ''}`}
                                onClick={() => setFilter(f)}
                            >
                                {filterLabel(f)}
                            </button>
                        ))}
                    </div>
                </div>
                {canEdit && (
                    <button className="activity-create-btn" onClick={() => setShowCreate(true)}>
                        + Create Request
                    </button>
                )}
            </div>

            {filtered.length === 0 ? (
                <div className="activity-empty">
                    <p>{emptyActivitiesText(filter)}</p>
                    {canEdit && filter === 'all' && (
                        <button className="activity-create-btn-empty" onClick={() => setShowCreate(true)}>
                            Create the first request
                        </button>
                    )}
                </div>
            ) : (
                <div className="activity-list">
                    {filtered.map(a => (
                        <ActivityCard key={a.id} activity={a} onStatusChange={handleStatusChange} onQuickStatus={handleQuickStatus} onAssign={handleAssign} onEdit={handleEdit} onDelete={handleDelete} users={users} activities={activities} canEdit={canEdit} />
                    ))}
                </div>
            )}

            {canEdit && showCreate && (
                <CreateRequestModal
                    draft={createDraft.current}
                    onDraft={(d) => { createDraft.current = d; }}
                    onDiscard={() => { createDraft.current = null; }}
                    unit={unit}
                    onClose={() => setShowCreate(false)}
                    onCreated={() => fetchActivities({ silent: true })}
                    activities={activities}
                />
            )}
            <TaskUndoToast toast={undoToast.toast} onUndo={undoToast.undo} onDismiss={undoToast.dismiss} />
        </div>
    );
}

ActivityTab.propTypes = {
    unit: PropTypes.object.isRequired,
    openCreate: PropTypes.bool,
    // The page's useUnitActivities instance, when it already holds one.
    activitiesState: PropTypes.object,
};

export default ActivityTab;
